import { EncoderDecoder } from '@iotize/common/converter/api';
import { ModbusOptions, TargetService } from '@iotize/tap/service/impl/target';

import { BLANK_CONVERTER } from '../../utility/blank-converter';
import {
  AbstractEditableDataStream,
  KeyTypeType,
} from '../../utility/editable-data-stream';
import { variableFormatToByteSize } from '../../utility/type-converter-factory';
import { AbstractVariable } from '../abstract-variable-stream';

type SubTargetService = Pick<TargetService, 'modbusRead' | 'modbusWrite'>;

export namespace ModbusAccessVariable {
  export interface InstanceOptions<DataType = Uint8Array> {
    converter?: EncoderDecoder<DataType, Uint8Array>;
    targetService: SubTargetService;
    config: ModbusOptions;
  }
}

export class ModbusAccessVariable<
  DataType,
  KeyType extends KeyTypeType,
> extends AbstractVariable<DataType, KeyType> {
  get converter() {
    return this.context.converter || BLANK_CONVERTER;
  }

  get config() {
    return this.context.config;
  }

  constructor(
    public id: KeyType,
    private context: ModbusAccessVariable.InstanceOptions<DataType>,
  ) {
    super();
  }

  async readRawInner(): Promise<Uint8Array> {
    return (
      await this.context.targetService.modbusRead(this.context.config)
    ).body();
  }

  async writeRaw(value: Uint8Array): Promise<void> {
    if (!value || !(value instanceof Uint8Array)) {
      throw new Error(
        `Bad parameter for ModbusAccessVariable.writeRaw(). Expecting an Uint8Array.`,
      );
    }
    const expectedByteSize =
      this.config.length * variableFormatToByteSize(this.config.format);
    if (value.length !== expectedByteSize) {
      throw new Error(
        `Encoded value does not have the expected byte size. Expecting ${expectedByteSize} byte(s) but given ${value.length} byte(s)`,
      );
    }
    return (
      await this.context.targetService.modbusWrite({
        data: value,
        options: this.context.config,
      })
    ).body();
  }
}
